import { Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import Modal from 'antd/lib/modal/Modal';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteCredential } from '../../fetchHooks/deleteCredential';
import { handleUpdateCredential } from '../../fetchHooks/handleUpdateCredential';
import { type CredentialType, useMyPasswords } from '../../fetchHooks/useMyPasswords';
import { ModalTitle } from '../MyDayPage/EditMyday';
import { ModalButtons } from '../MyDayPage/ModalButtons';
import styles from './myPasswords.module.css';

interface EditPasswordModalProps {
  show: boolean;
  onCancel: () => void;
  onOk: () => void;
  credential: CredentialType;
}

export const EditPasswordModal = ({ show, onCancel, onOk, credential }: EditPasswordModalProps) => {
  const [form] = useForm();
  const { refetch } = useMyPasswords();
  const { t } = useTranslation();
  useEffect(() => {
    form && credential && form.resetFields();
    form && credential && form.setFieldsValue({ ...credential, confirm: credential?.password });
  }, [form, credential]);

  if (!credential || !show) return <></>;

  return (
    <Modal
      open={show}
      styles={{ body: { minHeight: '50vh', padding: 0, borderRadius: '20px' } }}
      className={styles.createPasswordModal}
      onCancel={onCancel}
      destroyOnHidden
      footer={null}
      onOk={() => console.log('Validate Failed:')}
      forceRender
    >
      <ModalTitle title={t('passwords.editCredential')} />
      <Form
        form={form}
        name="create-password"
        className={styles.createPasswordForm}
        initialValues={{ website: '', password: '' }}
        scrollToFirstError
      >
        <Form.Item
          name="website"
          label={t('passwords.websiteService')}
          rules={[
            {
              required: true,
              message: t('passwords.pleaseInputWebsite'),
            },
          ]}
        >
          <Input autoComplete="off" list="autocompleteOff" />
        </Form.Item>
        <Form.Item
          name="username"
          label={t('passwords.username')}
          rules={[
            {
              required: true,
              message: t('passwords.pleaseInputUsername'),
            },
          ]}
        >
          <Input autoComplete="off" list="autocompleteOff" />
        </Form.Item>
        <Form.Item
          name="password"
          label={t('passwords.password')}
          rules={[
            {
              required: true,
              message: t('passwords.pleaseInputPassword'),
            },
          ]}
          hasFeedback
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirm"
          label={t('passwords.confirmPassword')}
          dependencies={['password']}
          hasFeedback
          rules={[
            {
              required: true,
              message: t('passwords.pleaseConfirmPassword'),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('passwords.passwordsDoNotMatch')));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item name="comment" label={t('passwords.comment')}>
          <Input.TextArea showCount maxLength={1000} />
        </Form.Item>
        <ModalButtons
          onOk={() => {
            form
              .validateFields()
              .then((values) => {
                handleUpdateCredential(values, credential, () => {
                  onOk?.();
                });
                form.resetFields();
              })
              .catch((info) => {
                console.log('Edit password: Validate Failed:', info);
              });
          }}
          onCancel={onCancel}
          okText={t('myday.save')}
          onDelete={() =>
            deleteCredential(credential?.id, () => {
              refetch();
              onCancel();
            })
          }
        />
      </Form>
    </Modal>
  );
};
